import { create } from "zustand"
import type { AuthMe, Balance, PvPGame } from "@/shared/api/types"

interface PlayerBet {
  id: string
  username?: string
  betAmount: string
  percent: string
}

interface GameState {
  // User
  user: AuthMe | null
  setUser: (user: AuthMe | null) => void

  // Balance
  balance: Balance | null
  setBalance: (balance: Balance | null) => void

  // Current game round
  currentRound: PvPGame | null
  setCurrentRound: (round: PvPGame | null) => void

  // Pool & Players
  prizePool: string
  setPrizePool: (pool: string) => void
  players: PlayerBet[]
  setPlayers: (players: PlayerBet[]) => void
  addPlayer: (player: PlayerBet) => void
  updatePlayerBet: (
    playerId: string,
    betAmount: string,
    percent: string
  ) => void

  // My bet in current round
  myBetAmount: number
  setMyBetAmount: (amount: number) => void
  myPoolShare: number
  setMyPoolShare: (share: number) => void

  // Betting
  selectedBetAmount: number
  setSelectedBetAmount: (amount: number) => void
  betAmounts: number[]
  setBetAmounts: (amounts: number[]) => void

  // UI State
  isPlacingBet: boolean
  setIsPlacingBet: (placing: boolean) => void

  // Wallet
  isWalletConnected: boolean
  setIsWalletConnected: (connected: boolean) => void
  walletAddress: string | null
  setWalletAddress: (address: string | null) => void

  // Reset round state
  resetRoundState: () => void
}

export const useGameStore = create<GameState>((set, get) => ({
  // User
  user: null,
  setUser: (user) => set({ user }),

  // Balance
  balance: null,
  setBalance: (balance) => set({ balance }),

  // Current game round
  currentRound: null,
  setCurrentRound: (currentRound) => set({ currentRound }),

  // Pool & Players
  prizePool: "0",
  setPrizePool: (prizePool) => set({ prizePool }),
  players: [],
  setPlayers: (players) => set({ players }),
  addPlayer: (player) =>
    set((state) => ({
      players: [...state.players.filter((p) => p.id !== player.id), player],
    })),
  updatePlayerBet: (playerId, betAmount, percent) =>
    set((state) => ({
      players: state.players.map((p) =>
        p.id === playerId ? { ...p, betAmount, percent } : p
      ),
    })),

  // My bet
  myBetAmount: 0,
  setMyBetAmount: (myBetAmount) => set({ myBetAmount }),
  myPoolShare: 0,
  setMyPoolShare: (myPoolShare) => set({ myPoolShare }),

  // Betting
  selectedBetAmount: 0.01,
  setSelectedBetAmount: (selectedBetAmount) => set({ selectedBetAmount }),
  betAmounts: [0.01, 0.1, 0.5, 1, 5, 10],
  setBetAmounts: (betAmounts) => set({ betAmounts }),

  // UI State
  isPlacingBet: false,
  setIsPlacingBet: (isPlacingBet) => set({ isPlacingBet }),

  // Wallet
  isWalletConnected: false,
  setIsWalletConnected: (isWalletConnected) => set({ isWalletConnected }),
  walletAddress: null,
  setWalletAddress: (walletAddress) => set({ walletAddress }),

  // Reset
  resetRoundState: () =>
    set({
      myBetAmount: 0,
      myPoolShare: 0,
      players: [],
      prizePool: "0",
    }),
}))
